import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import "./Template_3.css";
import Template_3_Header from "./Template_3_Header";
import Template_3_EnquiryPopup from "./Template_3_EnquiryPopup";
import Template_3_Footer from "./Template_3_Footer";
import Template_3_TopHeader from "./Template_3_TopHeader";

const Template_3_PackageDetails = () => {
  const { packageId } = useParams();
  const navigate = useNavigate();

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");
  const [showEnquiryPopup, setShowEnquiryPopup] = useState(false);
  const [imageGallery, setImageGallery] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const getPackageDetails = async () => {
      if (!packageId) return;
      setLoading(true);
      try {
        const response = await apiService.get(
          `${config.GETPACKAGEDETAILS}/${packageId}`
        );
        setDetails(response);

        const gallery = [response.package.imageSrc];
        if (response.hotelBookings) {
          response.hotelBookings.forEach((hotel) => {
            if (hotel.imageUrl) gallery.push(hotel.imageUrl);
          });
        }
        if (response.sightSeeings) {
          response.sightSeeings.forEach((sight) => {
            if (sight.imageUrl) gallery.push(sight.imageUrl);
          });
        }
        setImageGallery(gallery);
      } catch (err) {
        console.error("Failed to fetch package details:", err);
        setError("Could not load package details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    getPackageDetails();
  }, [packageId]);

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === imageGallery.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? imageGallery.length - 1 : prevIndex - 1
    );
  };

  if (loading)
    return (
      <div className="Template_3-loading-screen">
        <div className="Template_3-loader">
          <div className="Template_3-compass-icon">🧭</div>
          <p>Curating your bespoke experience...</p>
        </div>
      </div>
    );
  if (error) return <div className="Template_3-error">{error}</div>;
  if (!details)
    return <div className="Template_3-error">Experience not found</div>;

  const pkg = details.package || {};
  const itineraries = details.itineraries || [];
  const includes = details.includes || [];
  const excludes = details.excludes || [];
  const flightBookings = details.flightBookings || [];
  const hotelBookings = details.hotelBookings || [];
  const termsandConditions = details.termsandConditions || [];
  const transfers = details.transfers || [];
  const sightSeeings = details.sightSeeings || [];

  const getDestinationName = () => {
    if (!pkg.destination) return "Exclusive Locale";

    if (typeof pkg.destination === "string") return pkg.destination;

    if (pkg.destination.destinationName) return pkg.destination.destinationName;

    return "Exclusive Locale";
  };

  const destinationName = getDestinationName();

  return (
    <div className="Template_3 package-detail">
      <Template_3_TopHeader />
      <Template_3_Header />

      <div className="Template_3-hero">
        <div
          className="Template_3-hero-image"
          style={{ backgroundImage: `url(${imageGallery[currentImageIndex]})` }}
        >
          <div className="Template_3-hero-overlay">
            <div className="Template_3-hero-content">
              <div className="Template_3-hero-badges">
                <span className="Template_3-badge signature">
                  Signature Experience
                </span>
              </div>
              <h1 className="Template_3-hero-title">{pkg.packageName}</h1>
              <div className="Template_3-hero-details">
                <div className="Template_3-detail-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>{destinationName}</span>
                </div>
                <div className="Template_3-detail-item">
                  <i className="fas fa-clock"></i>
                  <span>{pkg.duration}</span>
                </div>
                <div className="Template_3-detail-item">
                  <i className="fas fa-user-friends"></i>
                  <span>{pkg.noOfPersons} Guests</span>
                </div>
              </div>
              <div className="Template_3-price-container">
                <div className="Template_3-price">
                  <span className="Template_3-price-from">From</span>
                  <span className="Template_3-price-amount">
                    ₹{pkg.price?.toLocaleString("en-IN")}
                  </span>
                  <span className="Template_3-price-person">per guest</span>
                </div>
              </div>
              <button
                className="Template_3-cta-button"
                onClick={() => setShowEnquiryPopup(true)}
              >
                <i className="fas fa-envelope"></i>
                Inquire Now
              </button>
            </div>
          </div>
        </div>

        {imageGallery.length > 1 && (
          <div className="Template_3-gallery-nav">
            <button className="Template_3-nav-btn" onClick={prevImage}>
              <i className="fas fa-chevron-left"></i>
            </button>
            <div className="Template_3-gallery-dots">
              {imageGallery.map((_, index) => (
                <button
                  key={index}
                  className={`Template_3-dot ${
                    index === currentImageIndex ? "active" : ""
                  }`}
                  onClick={() => setCurrentImageIndex(index)}
                />
              ))}
            </div>
            <button className="Template_3-nav-btn" onClick={nextImage}>
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        )}
      </div>

      <div className="Template_3-floating-nav">
        <div className="Template_3-nav-container">
          <button
            className={`Template_3-nav-item ${
              activeSection === "overview" ? "active" : ""
            }`}
            onClick={() => setActiveSection("overview")}
          >
            <i className="fas fa-binoculars"></i>
            Overview
          </button>
          <button
            className={`Template_3-nav-item ${
              activeSection === "itinerary" ? "active" : ""
            }`}
            onClick={() => setActiveSection("itinerary")}
          >
            <i className="fas fa-route"></i>
            Itinerary
          </button>
          <button
            className={`Template_3-nav-item ${
              activeSection === "accommodation" ? "active" : ""
            }`}
            onClick={() => setActiveSection("accommodation")}
          >
            <i className="fas fa-hotel"></i>
            Accommodations
          </button>
          <button
            className={`Template_3-nav-item ${
              activeSection === "inclusions" ? "active" : ""
            }`}
            onClick={() => setActiveSection("inclusions")}
          >
            <i className="fas fa-check-circle"></i>
            Inclusions
          </button>
          <button
            className={`Template_3-nav-item ${
              activeSection === "gallery" ? "active" : ""
            }`}
            onClick={() => setActiveSection("gallery")}
          >
            <i className="fas fa-images"></i>
            Gallery
          </button>
        </div>
      </div>

      <div className="Template_3-main-content">
        <div className="Template_3-content-container">
          <div className="Template_3-content-grid">
            <div className="Template_3-main-column">
              {activeSection === "overview" && (
                <div className="Template_3-content-section">
                  <h2 className="Template_3-section-title">
                    Experience Overview
                  </h2>
                  <div className="Template_3-description">
                    {pkg.description && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: pkg.description.replace(/\r\n/g, "<br/>"),
                        }}
                      />
                    )}
                  </div>

                  {sightSeeings.length > 0 && (
                    <div className="Template_3-highlights">
                      <h3 className="Template_3-subtitle">Signature Moments</h3>
                      <div className="Template_3-highlights-list">
                        {sightSeeings.map((sight, i) => (
                          <div key={i} className="Template_3-highlight-item">
                            <i className="fas fa-star"></i>
                            <div>
                              <h4>{sight.sightseeingName}</h4>
                              <p>{sight.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeSection === "itinerary" && (
                <div className="Template_3-content-section">
                  <h2 className="Template_3-section-title">
                    Day-by-Day Journey
                  </h2>
                  <div className="Template_3-itinerary">
                    {itineraries.map((day, i) => (
                      <div key={i} className="Template_3-itinerary-day">
                        <div className="Template_3-day-header">
                          <div className="Template_3-day-number">
                            Day {i + 1}
                          </div>
                          <div className="Template_3-day-location">
                            <i className="fas fa-map-marker-alt"></i>
                            {typeof day.location === "object"
                              ? day.location.destinationName ||
                                "Exclusive Location"
                              : day.location}
                          </div>
                        </div>
                        <div className="Template_3-day-content">
                          <p>{day.description}</p>
                          {day.meals && (
                            <div className="Template_3-day-meals">
                              <i className="fas fa-utensils"></i>
                              <strong>Culinary Inclusions:</strong> {day.meals}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeSection === "accommodation" && (
                <div className="Template_3-content-section">
                  <h2 className="Template_3-section-title">
                    Luxurious Accommodations
                  </h2>

                  {hotelBookings.length > 0 && (
                    <div className="Template_3-hotels">
                      {hotelBookings.map((hotel, i) => (
                        <div key={i} className="Template_3-hotel-card">
                          <div className="Template_3-hotel-details">
                            <h3>{hotel.hotelName}</h3>
                            <div className="Template_3-hotel-location">
                              <i className="fas fa-map-marker-alt"></i>
                              {typeof hotel.city === "object"
                                ? hotel.city.destinationName || "Premier City"
                                : hotel.city}
                            </div>
                            <div className="Template_3-hotel-features">
                              <span className="Template_3-feature-tag">
                                <i className="fas fa-bed"></i> {hotel.roomType}
                              </span>
                              <span className="Template_3-feature-tag">
                                <i className="fas fa-moon"></i> {hotel.nights}{" "}
                                Nights
                              </span>
                            </div>
                            <div className="Template_3-hotel-rating">
                              {Array.from({ length: 5 }).map((_, idx) => (
                                <i
                                  key={idx}
                                  className={`fas fa-star ${
                                    idx < 5 ? "filled" : ""
                                  }`}
                                ></i>
                              ))}
                              <span className="Template_3-rating-text">
                                5.0/5
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {flightBookings.length > 0 && (
                    <div className="Template_3-flights">
                      <h3 className="Template_3-subtitle">Private Aviation</h3>
                      {flightBookings.map((flight, i) => (
                        <div key={i} className="Template_3-flight-card">
                          <div className="Template_3-flight-header">
                            <div className="Template_3-airline">
                              <i className="fas fa-plane"></i>
                              {flight.airline}
                            </div>
                            <div className="Template_3-flight-number">
                              {flight.flightNumber}
                            </div>
                          </div>
                          <div className="Template_3-flight-route">
                            <div className="Template_3-departure">
                              <div className="Template_3-city">
                                {typeof flight.departureCity === "object"
                                  ? flight.departureCity.destinationName ||
                                    "Origin"
                                  : flight.departureCity}
                              </div>
                            </div>
                            <div className="Template_3-connection">
                              <i className="fas fa-long-arrow-alt-right"></i>
                            </div>
                            <div className="Template_3-arrival">
                              <div className="Template_3-city">
                                {typeof flight.arrivalCity === "object"
                                  ? flight.arrivalCity.destinationName ||
                                    "Destination"
                                  : flight.arrivalCity}
                              </div>
                            </div>
                          </div>
                          <div className="Template_3-flight-class">
                            <span className="Template_3-class-badge">
                              {flight.cabinClass}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {transfers.length > 0 && (
                    <div className="Template_3-transfers">
                      <h3 className="Template_3-subtitle">
                        Seamless Transfers
                      </h3>
                      <div className="Template_3-transfers-list">
                        {transfers.map((transfer, i) => (
                          <div key={i} className="Template_3-transfer-item">
                            <i className="fas fa-car"></i>
                            <div>
                              <h4>Transfer {i + 1}</h4>
                              <p>
                                <strong>From:</strong>{" "}
                                {typeof transfer.fromLocation === "object"
                                  ? transfer.fromLocation.destinationName ||
                                    "Origin"
                                  : transfer.fromLocation}
                              </p>
                              <p>
                                <strong>To:</strong>{" "}
                                {typeof transfer.toLocation === "object"
                                  ? transfer.toLocation.destinationName ||
                                    "Destination"
                                  : transfer.toLocation}
                              </p>
                              <p>
                                <strong>Vehicle:</strong>{" "}
                                {transfer.vehicleDetails}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeSection === "inclusions" && (
                <div className="Template_3-content-section">
                  <h2 className="Template_3-section-title">
                    Included Amenities
                  </h2>

                  <div className="Template_3-inclusions-exclusions">
                    <div className="Template_3-inclusions">
                      <h3 className="Template_3-subtitle">
                        <i className="fas fa-check-circle"></i>
                        Included Privileges
                      </h3>
                      <ul className="Template_3-inclusion-list">
                        {includes.map((item, idx) => (
                          <li key={idx}>
                            <i className="fas fa-check"></i>
                            {item.description}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="Template_3-exclusions">
                      <h3 className="Template_3-subtitle">
                        <i className="fas fa-times-circle"></i>
                        Exclusions
                      </h3>
                      <ul className="Template_3-exclusion-list">
                        {excludes.map((item, idx) => (
                          <li key={idx}>
                            <i className="fas fa-times"></i>
                            {item.description}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {termsandConditions.length > 0 && (
                    <div className="Template_3-terms">
                      <h3 className="Template_3-subtitle">
                        Terms & Conditions
                      </h3>
                      <div className="Template_3-terms-list">
                        {termsandConditions.map((term, i) => (
                          <div key={i} className="Template_3-term-item">
                            <h4>{term.title}</h4>
                            <p>{term.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeSection === "gallery" && (
                <div className="Template_3-content-section">
                  <h2 className="Template_3-section-title">Visual Portfolio</h2>
                  <div className="Template_3-gallery-grid">
                    {imageGallery.map((img, index) => (
                      <div
                        key={index}
                        className="Template_3-gallery-item"
                        style={{ backgroundImage: `url(${img})` }}
                        onClick={() => setCurrentImageIndex(index)}
                      >
                        <div className="Template_3-gallery-overlay">
                          <i className="fas fa-search-plus"></i>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="Template_3-sidebar-column">
              <div className="Template_3-booking-widget">
                <div className="Template_3-widget-header">
                  <h3>Inquire About This Experience</h3>
                  <div className="Template_3-widget-price">
                    <span className="Template_3-price-from">From</span>
                    <span className="Template_3-price-amount">
                      ₹{pkg.price?.toLocaleString("en-IN")}
                    </span>
                    <span className="Template_3-price-desc">per guest</span>
                  </div>
                </div>

                <div className="Template_3-widget-details">
                  <div className="Template_3-detail-row">
                    <div className="Template_3-detail-icon">
                      <i className="fas fa-clock"></i>
                    </div>
                    <div className="Template_3-detail-content">
                      <span className="Template_3-detail-label">Duration</span>
                      <span className="Template_3-detail-value">
                        {pkg.duration}
                      </span>
                    </div>
                  </div>

                  <div className="Template_3-detail-row">
                    <div className="Template_3-detail-icon">
                      <i className="fas fa-users"></i>
                    </div>
                    <div className="Template_3-detail-content">
                      <span className="Template_3-detail-label">
                        Group Size
                      </span>
                      <span className="Template_3-detail-value">
                        {pkg.noOfPersons} Guests
                      </span>
                    </div>
                  </div>

                  <div className="Template_3-detail-row">
                    <div className="Template_3-detail-icon">
                      <i className="fas fa-user-tie"></i>
                    </div>
                    <div className="Template_3-detail-content">
                      <span className="Template_3-detail-label">Concierge</span>
                      <span className="Template_3-detail-value">
                        {pkg.contactName}
                      </span>
                    </div>
                  </div>

                  <div className="Template_3-detail-row">
                    <div className="Template_3-detail-icon">
                      <i className="fas fa-phone"></i>
                    </div>
                    <div className="Template_3-detail-content">
                      <span className="Template_3-detail-label">Contact</span>
                      <span className="Template_3-detail-value">
                        {pkg.contactPhoneNumber}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="Template_3-widget-actions">
                  <button
                    className="Template_3-primary-btn"
                    onClick={() => setShowEnquiryPopup(true)}
                  >
                    <i className="fas fa-envelope"></i>
                    Inquire Now
                  </button>
                </div>

                <div className="Template_3-widget-features">
                  <div className="Template_3-feature">
                    <i className="fas fa-shield-alt"></i>
                    <span>Flexible Cancellation</span>
                  </div>
                  <div className="Template_3-feature">
                    <i className="fas fa-credit-card"></i>
                    <span>No Hidden Fees</span>
                  </div>
                  <div className="Template_3-feature">
                    <i className="fas fa-headset"></i>
                    <span>Dedicated Support</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showEnquiryPopup && (
        <Template_3_EnquiryPopup
          packageDetails={pkg}
          onClose={() => setShowEnquiryPopup(false)}
        />
      )}

      <Template_3_Footer />
    </div>
  );
};

export default Template_3_PackageDetails;
